import React from 'react';

import {
  Datagrid,
  TextField,
  List,
  ArrayField,
  SingleFieldList,
  SelectInput,
  usePermissions,
  ArrayInput,
} from 'react-admin';

import {
  Card,
} from '@mui/material';

import NoResults from '../layout/NoResults';
import Pagination from '../layout/Pagination';

import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import ColoredScoreField from '../../customFields/ColoredScoreField';
import LinkField from '../layout/LinkField';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import { countryChoices } from '../../constants/countries';
import languages from '../../constants/languages';
import { IAccountEntity } from '../../utilities/schemas/accountEntity';
import ChipStringField from '../../customFields/ChipStringField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import { ReferenceManyDisplayField } from '../../customFields/EditReferenceManyField';
import SearchField from '../../customFields/SearchField';
import EntityFilter from '../filters/EntityFilter';
import entityTypes from '../../constants/entityTypes';
import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';
import GroupFilter from '../utils/GroupFilter';

export const AccountEntityListBody = ({ children }: {children?: React.ReactNode}) => {
  const { permissions } = usePermissions<Actions[]>();

  return (
    <Datagrid bulkActionButtons={false} sx={boldDataGridStyle} empty={<NoResults variant="h6" />}>
      {hasAccess(permissions, actions.ACCOUNT_ENTITY_GET) ? (
        <LinkField<IAccountEntity> sortable={false} source="id" label="ID" />
      ) : (
        <TextField sortable={false} source="id" label="ID" />
      )}
      <ArrayField sortable={false} source="customIds" label="Custom IDs">
        <SingleFieldList linkType={false}>
          <ChipStringField />
        </SingleFieldList>
      </ArrayField>
      {hasAccess(permissions, actions.GROUP_LIST) && (
        <ReferenceManyDisplayField
          label="Groups"
          reference="groups"
          source="groups"
        />
      )}
      <TextField<IAccountEntity> sortable={false} source="accountName" emptyText="-" />
      <TextField<IAccountEntity> sortable={false} source="iban" label="IBAN" emptyText="-" />
      <ArrayField<IAccountEntity> sortable={false} source="connectedEntities" label="Account holders">
        <SingleFieldList linkType={false}>
          <ReferenceEntityField source="">
            <EntityDisplayNameField chip />
          </ReferenceEntityField>
        </SingleFieldList>
      </ArrayField>
      <ColoredScoreField<IAccountEntity> source="riskScore" />
      {children}
    </Datagrid>
  );
};

export const AccountEntityFilters = [
  <SearchField source="search" label="Search" alwaysOn />,
  <ArrayInput source="groups" label="Groups">
    <GroupFilter />
  </ArrayInput>,
  <EntityFilter
    label="Connected Entities"
    source="connectedEntities.id"
    allowedTypes={[entityTypes.INDIVIDUAL, entityTypes.BUSINESS]}
  />,
  <SelectInput choices={countryChoices(languages.EN)} source="country" label="Country" />,
];

const AccountEntityList = () => (
  <List<IAccountEntity>
    sort={{ field: 'createdAt', order: 'DESC' }}
    empty={false}
    pagination={<Pagination disableEmptyText />}
    filters={AccountEntityFilters}
    exporter={false}
    perPage={50}
  >
    <Card sx={{ margin: 0 }} variant="outlined">
      <AccountEntityListBody />
    </Card>
  </List>
);

export default AccountEntityList;
